export const COVER_ASPECT = 2 / 3;
export const COVER_MAX_OUTPUT_WIDTH = 800;
export const COVER_MAX_ZOOM = 4;

export interface CropView {
  zoom: number;
  centerX: number;
  centerY: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface CropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function initialCropView(image: Size): CropView {
  return { zoom: 1, centerX: image.width / 2, centerY: image.height / 2 };
}

/**
 * Region of the source image (in source pixels) visible through the 2:3 frame.
 * At zoom 1 the frame is cover-fitted; the centre is clamped so the frame never leaves the image.
 */
export function getCropRegion(image: Size, view: CropView): CropRegion {
  const zoom = Math.min(COVER_MAX_ZOOM, Math.max(1, view.zoom));
  const fitsWidth = image.width / image.height <= COVER_ASPECT;
  const baseWidth = fitsWidth ? image.width : image.height * COVER_ASPECT;
  const width = baseWidth / zoom;
  const height = width / COVER_ASPECT;
  const x = Math.min(image.width - width, Math.max(0, view.centerX - width / 2));
  const y = Math.min(image.height - height, Math.max(0, view.centerY - height / 2));
  return { x, y, width, height };
}

export function panCropView(image: Size, view: CropView, deltaFramePx: { x: number; y: number }, framePxWidth: number): CropView {
  const region = getCropRegion(image, view);
  const sourcePerPx = region.width / framePxWidth;
  const next = {
    zoom: view.zoom,
    centerX: region.x + region.width / 2 - deltaFramePx.x * sourcePerPx,
    centerY: region.y + region.height / 2 - deltaFramePx.y * sourcePerPx,
  };
  const clamped = getCropRegion(image, next);
  return { zoom: next.zoom, centerX: clamped.x + clamped.width / 2, centerY: clamped.y + clamped.height / 2 };
}

export function zoomCropView(image: Size, view: CropView, zoom: number): CropView {
  const region = getCropRegion(image, view);
  const next = { zoom: Math.min(COVER_MAX_ZOOM, Math.max(1, zoom)), centerX: region.x + region.width / 2, centerY: region.y + region.height / 2 };
  const clamped = getCropRegion(image, next);
  return { zoom: next.zoom, centerX: clamped.x + clamped.width / 2, centerY: clamped.y + clamped.height / 2 };
}

export function getOutputSize(region: CropRegion): Size {
  const width = Math.min(COVER_MAX_OUTPUT_WIDTH, Math.round(region.width));
  return { width, height: Math.round(width / COVER_ASPECT) };
}
